import { ElementRef, EventEmitter, Injectable } from '@angular/core';
import { ChannelPanel } from '@components/channel/side-menu/side-menu.component';
import { ChannelGroup } from 'icreate/common/models/channel-group';
import { BehaviorSubject, Subject } from 'rxjs';
import _ from 'lodash';
import { ChannelFns } from 'icreate/common/models/channel';
import { ChannelType } from 'icreate/common/models/channel-type';
import { TranslateService } from '@ngx-translate/core';
import { ErrorCodes } from 'icreate/common/excel-import/error';

@Injectable({
    providedIn: 'root',
})
export class ChannelSheetService {
    private activeGroupSubject = new BehaviorSubject<ChannelGroup | null>(null);
    public activeGroup$ = this.activeGroupSubject.asObservable();

    private selectedChannelSubject = new BehaviorSubject<any>('');
    public selectedChannel$ = this.selectedChannelSubject.asObservable();

    private isChannelUpdatedSubject = new BehaviorSubject<boolean>(false);
    public isChannelUpdated$ = this.isChannelUpdatedSubject.asObservable();

    private tabStateSubject = new BehaviorSubject<ChannelPanel>(ChannelPanel.GROUP);
    public tabState$ = this.tabStateSubject.asObservable();

    private isAlarmUpdatedSubject = new BehaviorSubject<boolean>(false);
    public isAlarmUpdated$ = this.isAlarmUpdatedSubject.asObservable();

    private updateChannelSubject = new BehaviorSubject<any>(false);
    public updateChannel$ = this.updateChannelSubject.asObservable();

    private channelsUpdatedSubject = new BehaviorSubject<any>(false);
    public channelsUpdated$ = this.channelsUpdatedSubject.asObservable();

    private saveStartedSubject = new Subject<void>();
    public saveStarted$ = this.saveStartedSubject.asObservable();

    private validationEnabledSubject = new BehaviorSubject<boolean>(false);
    public validationEnabled$ = this.validationEnabledSubject.asObservable();

    loadingEmitter = new EventEmitter<boolean>();

    private sideMenu: ElementRef;

    constructor(private translateSerivce: TranslateService) {}

    setUpdateChannel(updatedChannel: any) {
        this.updateChannelSubject.next(updatedChannel);
    }

    setIsAlarmUpdate(isAlarmUpdated: boolean) {
        this.isAlarmUpdatedSubject.next(isAlarmUpdated);
    }

    setTabState(tabState: ChannelPanel) {
        this.tabStateSubject.next(tabState);
    }

    setIsChannelUpdated(isChannelUpdated: boolean) {
        this.isChannelUpdatedSubject.next(isChannelUpdated);
    }

    getIsChannelUpdated() {
        return this.isChannelUpdatedSubject.value;
    }

    setValidationEnabled(isValidationEnabled: boolean) {
        this.validationEnabledSubject.next(isValidationEnabled);
    }

    getIsValidationEnabled() {
        return this.validationEnabledSubject.value;
    }

    setSelectedChannel(channel: any) {
        this.selectedChannelSubject.next(channel);
    }

    setActiveGroup(channelGroup: ChannelGroup) {
        this.activeGroupSubject.next(channelGroup);
    }

    setChannelsUpdated(isChannelsUpdated: boolean) {
        this.channelsUpdatedSubject.next(isChannelsUpdated);
    }

    notifySaveStarted() {
        this.saveStartedSubject.next();
    }

    showLoading() {
        this.loadingEmitter.emit(true);
    }

    hideLoading() {
        this.loadingEmitter.emit(false);
    }

    setSideBarElement(sideMenuElement) {
        this.sideMenu = sideMenuElement;
    }

    getSideMenu() {
        return this.sideMenu;
    }

    initChannelRows(channelGroup: ChannelGroup, initialRows: number = 300) {
        const newChannelList = new Array(initialRows)
            .fill(null)
            .map((_, index) => this.createNewChannel(index, channelGroup));

        const channelExcelIds = new Set();
        newChannelList.forEach(channel => {
            let combinedValue: number;
            do {
                let randomValue = Math.floor(8388607 * Math.random() + 1);
                let groupNum = channelGroup.rowNum & 0xff;
                combinedValue = (randomValue * 0x100) | groupNum;
            } while (channelExcelIds.has(combinedValue));
            channelExcelIds.add(combinedValue);
            channel.rowId = combinedValue.toString();
        });
        return newChannelList;
    }

    createNewChannel(rowIndex, channelGroup: ChannelGroup) {
        const newChannel = ChannelFns.create(true);
        newChannel.rowIndex = rowIndex;
        newChannel.groupId = channelGroup.id;
        newChannel.vesselId = channelGroup.vesselId;

        ChannelFns.setChannelType(newChannel, ChannelType.BLANK);
        return newChannel;
    }

    getImportErrorMessage(code: ErrorCodes, option): string {
        let text = '';
        switch (code) {
            case ErrorCodes.UNKNOWN_HEADER:
                text = 'excel-channel-import.errors.error-header';
                break;
            case ErrorCodes.MISSING_HEADER:
                text = 'excel-channel-import.errors.MISSING_HEADER';
                break;
            case ErrorCodes.MISSING_REQUIRED_VALUE:
                text = 'excel-channel-import.errors.MISSING_REQUIRED_VALUE';
                break;
            case ErrorCodes.VALUE_NOT_ALLOWED:
                text = 'excel-channel-import.errors.VALUE_NOT_ALLOWED';
                break;
            case ErrorCodes.INVALID_STATUS_STRING:
                text = 'excel-channel-import.errors.INVALID_STATUS_STRING';
                break;
            case ErrorCodes.ALARM_OVERLAP:
                text = 'excel-channel-import.errors.ALARM_OVERLAP';
                break;
            case ErrorCodes.INVALID_RANGE:
                text = 'excel-channel-import.errors.INVALID_RANGE';
                break;
            case ErrorCodes.NORMAL_RANGE_OUTSIDE_MEASURABLE_RANGE:
                text = 'excel-channel-import.errors.NORMAL_RANGE_OUTSIDE_MEASURABLE_RANGE';
                break;
            case ErrorCodes.ALARM_OUTSIDE_MEASURABLE_RANGE:
                text = 'excel-channel-import.errors.ALARM_OUTSIDE_MEASURABLE_RANGE';
                break;
            case ErrorCodes.MISSING_CATEGORY:
                text = 'excel-channel-import.errors.MISSING_CATEGORY';
                break;
            case ErrorCodes.MISSING_CATEGORY_SHEET:
                text = 'excel-channel-import.errors.MISSING_CATEGORY_SHEET';
                break;
            case ErrorCodes.UNIQUENESS_VALUE:
                text = 'excel-channel-import.errors.UNIQUENESS_VALUE';
                break;
            case ErrorCodes.MAX_CHANNEL_COUNT_EXCEEDED:
                text = 'excel-channel-import.errors.MAX_CHANNEL_COUNT_EXCEEDED';
                break;
            case ErrorCodes.UNKNOWN_ERROR:
                text = 'excel-channel-import.errors.UNKNOWN_ERROR';
                break;
            default:
                text = 'excel-channel-import.errors.UNKNOWN_ERROR';
        }

        return this.translateSerivce.instant(text, option);
    }
}

export enum UpdateType {
    update,
    delete,
    add,
}
